import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { useFrontendAuth } from '../hooks/useAuth';
import { getUserStatus, getUserStrikes, UserStatusResponse } from '../services/api.shop';
import { AccountStatus as AccountStatusEnum, StrikeDto } from '../types';
import { translateWithFallback } from '../utils/translateWithFallback';
import LoadingSpinner from '../components/LoadingSpinner';

type LocationState = { status?: string | number; message?: string } | null;

/** Normalize API/location status (string enum or numeric) to a display key. */
function normalizeStatus(raw: unknown): AccountStatusEnum | 'Unknown' {
    if (raw === AccountStatusEnum.OK || raw === 'OK' || raw === 0) return AccountStatusEnum.OK;
    if (raw === AccountStatusEnum.Warning || raw === 'Warning' || raw === 1) return AccountStatusEnum.Warning;
    if (raw === AccountStatusEnum.Suspended || raw === 'Suspended' || raw === 2) return AccountStatusEnum.Suspended;
    if (raw === AccountStatusEnum.Banned || raw === 'Banned' || raw === 3) return AccountStatusEnum.Banned;
    if (typeof raw === 'string' && raw.length > 0 && raw !== 'Unknown') return raw as AccountStatusEnum;
    return 'Unknown';
}

function statusLabel(status: AccountStatusEnum | 'Unknown', tr: (k: string, f: string) => string): string {
    switch (status) {
        case AccountStatusEnum.OK:
            return tr('account.status.label.ok', 'in good standing');
        case AccountStatusEnum.Warning:
            return tr('account.status.label.warning', 'under warning');
        case AccountStatusEnum.Suspended:
            return tr('account.status.label.suspended', 'suspended');
        case AccountStatusEnum.Banned:
            return tr('account.status.label.banned', 'banned');
        default:
            return tr('account.status.label.unknown', 'unknown');
    }
}

/**
 * Account status page used from:
 * - Login block redirects (location.state)
 * - Trust emails linking here (must load live status when logged in)
 */
const AccountStatus = () => {
    const location = useLocation();
    const state = (location.state as LocationState) ?? null;
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);
    const { user, isAuthenticated, isAuthReady } = useFrontendAuth();

    const [loading, setLoading] = useState(false);
    const [liveStatus, setLiveStatus] = useState<AccountStatusEnum | 'Unknown' | null>(null);
    const [accessLevel, setAccessLevel] = useState<UserStatusResponse['accessLevel'] | null>(null);
    const [strikes, setStrikes] = useState<StrikeDto[]>([]);
    const [fetchError, setFetchError] = useState<string | null>(null);

    useEffect(() => {
        if (!isAuthReady || !isAuthenticated) return;

        let cancelled = false;
        setLoading(true);
        setFetchError(null);

        (async () => {
            try {
                // Prefer auth/me user if present (includes status + strikes)
                if (user?.status != null) {
                    if (!cancelled) {
                        setLiveStatus(normalizeStatus(user.status));
                        setStrikes(user.strikes ?? []);
                    }
                }

                const [statusPayload, strikeList] = await Promise.all([
                    getUserStatus().catch(() => null),
                    getUserStrikes().catch(() => [] as StrikeDto[]),
                ]);

                if (cancelled) return;
                if (statusPayload != null) {
                    setLiveStatus(normalizeStatus(statusPayload.status));
                    setAccessLevel(statusPayload.accessLevel);
                }
                if (Array.isArray(strikeList)) setStrikes(strikeList);
            } catch {
                if (!cancelled) {
                    setFetchError(tr('account.status.loadError', 'Could not load your account status.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthReady, isAuthenticated, user?.status, user?.strikes]);

    if (!isAuthReady) {
        return <LoadingSpinner />;
    }

    // Logged out: use login redirect state, or invite to sign in (email deep link)
    if (!isAuthenticated) {
        const fromLogin = normalizeStatus(state?.status);
        const hasLoginState = state?.status != null && fromLogin !== 'Unknown';

        return (
            <div className="mx-auto mt-10 max-w-lg px-4 text-center sm:mt-16">
                <h1 className="mb-4 text-2xl font-bold sm:text-3xl">{tr('account.status.title', 'Account status')}</h1>
                {hasLoginState ? (
                    <>
                        <p className="mb-2 text-xl">
                            {tr('account.status.youraccountis', 'Your account is')}{' '}
                            <strong>{statusLabel(fromLogin, tr)}</strong>.
                        </p>
                        {state?.message && <p className="mb-6 text-gray-700">{state.message}</p>}
                    </>
                ) : (
                    <p className="mb-6 text-gray-700">
                        {tr(
                            'account.status.loginRequired',
                            'Sign in to view your current account standing and any strikes.'
                        )}
                    </p>
                )}
                <p className="mb-6 text-gray-600">
                    {tr('account.status.contactsupport', 'Please contact support for assistance.')}
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                    <Link
                        to="/login"
                        className="rounded-full bg-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-white"
                    >
                        {tr('page.login.title', 'Log in')}
                    </Link>
                    <Link to="/helpdesk" className="rounded-full border border-[var(--accent-color)] px-5 py-2 text-sm font-medium">
                        {tr('form.profile.helpdesk', 'Helpdesk')}
                    </Link>
                </div>
            </div>
        );
    }

    if (loading && liveStatus == null) {
        return <LoadingSpinner />;
    }

    const status = liveStatus ?? normalizeStatus(user?.status) ?? normalizeStatus(state?.status);
    const displayStrikes = strikes.length > 0 ? strikes : (user?.strikes ?? []);

    return (
        <div className="mx-auto mt-10 max-w-lg px-4 text-center sm:mt-12">
            <h1 className="mb-4 text-2xl font-bold sm:text-3xl">{tr('account.status.title', 'Account status')}</h1>
            <p className="mb-2 text-xl">
                {tr('account.status.youraccountis', 'Your account is')}{' '}
                <strong>{statusLabel(status, tr)}</strong>.
            </p>

            {status === AccountStatusEnum.OK && displayStrikes.length > 0 && (
                <p className="mb-4 text-amber-700">
                    {tr(
                        'account.status.okWithStrikes',
                        `You have ${displayStrikes.length} strike(s) on record, but your account is active.`
                    ).replace('{count}', String(displayStrikes.length))}
                </p>
            )}

            {status === AccountStatusEnum.Suspended && (
                <p className="mb-4 font-medium text-red-600">
                    {tr(
                        'account.suspended',
                        `Your account is suspended due to ${displayStrikes.length} strikes.`
                    ).replace('{count}', String(displayStrikes.length))}
                </p>
            )}

            {status === AccountStatusEnum.Banned && (
                <div className="mb-4 space-y-2 font-medium text-red-700">
                    <p>
                        {tr('account.status.bannedDetail', 'Your account has been banned from the marketplace.')}
                    </p>
                    {accessLevel === 'LimitedWindDown' && (
                        <p className="text-sm font-normal text-amber-800">
                            {tr(
                                'account.status.limitedWindDown',
                                'You still have limited access to finish open purchases and sales until they are delivered or completed. You cannot create new listings or start new interactions.'
                            )}
                        </p>
                    )}
                    {accessLevel === 'LockedOut' && (
                        <p className="text-sm font-normal text-red-800">
                            {tr(
                                'account.status.lockedOut',
                                'You have no open orders left. Marketplace access is fully locked. Contact support if you need help.'
                            )}
                        </p>
                    )}
                </div>
            )}

            {fetchError && <p className="mb-4 text-red-600">{fetchError}</p>}

            {displayStrikes.length > 0 && (
                <ul className="mb-6 list-disc space-y-2 pl-6 text-left text-sm text-gray-800">
                    {displayStrikes.map((strike) => (
                        <li key={strike.id}>
                            {strike.reason}{' '}
                            <span className="text-gray-500">
                                ({tr('account.centre.issued', 'issued')}:{' '}
                                {new Date(strike.created ?? '').toLocaleDateString()})
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <p className="mb-6 text-gray-600">
                {tr('account.status.contactsupport', 'Please contact support for assistance.')}
            </p>

            <div className="flex flex-wrap justify-center gap-3">
                {(status === AccountStatusEnum.OK || status === AccountStatusEnum.Warning) && (
                    <Link
                        to="/account-centre"
                        className="rounded-full bg-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-white"
                    >
                        {tr('account.centre.title', 'Account health')}
                    </Link>
                )}
                {accessLevel === 'LimitedWindDown' && (
                    <Link
                        to="/my-account/orders"
                        className="rounded-full bg-[var(--accent-color)] px-5 py-2 text-sm font-semibold text-white"
                    >
                        {tr('account.status.openOrders', 'Open orders')}
                    </Link>
                )}
                <Link to="/helpdesk" className="rounded-full border border-[var(--accent-color)] px-5 py-2 text-sm font-medium">
                    {tr('form.profile.helpdesk', 'Helpdesk')}
                </Link>
                <Link to="/my-account" className="text-sm underline text-gray-600">
                    {tr('account.hub.back', 'Back to account')}
                </Link>
            </div>
        </div>
    );
};

export default AccountStatus;
